import axios from 'axios';
import { buildApiUrl } from '../config/api.config';
import { blobticService } from './blobticService';

export interface UserActionEntry {
  UAD_IDF: number;
  CNX_IDF?: number | null;
  USER_IDF?: number | null;
  USER_MAIL?: string | null;
  UAD_METHOD: string;
  UAD_PATH: string;
  UAD_QUERY?: string | null;
  UAD_BODY?: string | null;
  UAD_OKEY?: string | null;
  UAD_START: string;
  UAD_END?: string | null;
  UAD_DURATION_MS: number;
  UAD_SOURCE?: string | null;
  UAD_STATUS: number;
  UAD_IP?: string | null;
  UAD_USER_AGENT?: string | null;
  UAD_CORRELATION_ID?: string | null;
  UAD_TOKEN_HASH?: string | null;
  UAD_ERROR?: string | null;
  CNX_UIDF?: string | null;
  CNX_NUM?: string | null;
  CNX_APPLICATION?: string | null;
  CNXW_WID?: string | null;
}

export interface UserActionStatistics {
  total_requests: number;
  connected_requests: number;
  public_requests: number;
  internal_requests: number;
  avg_duration_ms: number;
  max_duration_ms: number;
  server_errors: number;
  distinct_users: number;
  distinct_ips: number;
  distinct_okeys: number;
}

const USER_ACTION_SELECT_OKEY = 'Blobtic_Sys_User,user_action,select';

export interface UserActionFilters {
  statistics?: boolean;
  hours?: number;
  limit?: number;
  source?: string;
  method?: string;
  status?: string;
  userId?: number;
  cnxIdf?: number;
  okey?: string;
  text?: string;
  onlyConnected?: boolean;
  start?: string;
  end?: string;
}

export interface UserConnexion {
  CNX_IDF: number;
  CNX_UIDF?: string | null;
  CNX_NUM?: string | null;
  CNX_DATD?: string | null;
  CNX_DATF?: string | null;
  CNXW_DATF?: string | null;
  CNX_ACTIVE?: number | null;
  CNX_TEXT?: string | null;
  CNXW_APPLICATION?: string | null;
  CNXW_WID?: string | null;
  CNXW_OPENED_IDF?: string | null;
  CNXW_DOS_IDF?: number | null;
  CNXW_CLI_IDF?: number | null;
  CNX_COMPUTERNAME?: string | null;
  CNXW_REFER?: string | null;
  CNXW_REMOTEIP?: string | null;
  CNXW_HOST?: string | null;
  CNXW_USERAGENT?: string | null;
  CNXW_LANGUAGE?: string | null;
  CNXW_USR_LANGUAGE?: string | null;
  USR_IDF?: number | null;
  USR_CODE?: string | null;
  USR_LIB?: string | null;
  USR_MAIL?: string | null;
  DOS_IDF?: number | null;
  DOS_CODE?: string | null;
  DOS_LIB?: string | null;
  ActionCount?: number;
}

async function runSelect(params: UserActionFilters): Promise<any[]> {
  const res = await blobticService.executeZoomRequest<any[]>({
    // boubaker 22/08/2026 - données volatiles (actions/connexions temps réel) : jamais de cache IndexDB
    useCache: false,
    ZoomKey: 'user_action',
    Action: {
      OKey: USER_ACTION_SELECT_OKEY,
      statistics: params.statistics,
      hours: params.hours ?? 24,
      limit: params.limit ?? 200,
      source: params.source,
      method: params.method,
      status: params.status,
      userId: params.userId,
      cnxIdf: params.cnxIdf,
      okey: params.okey,
      text: params.text,
      onlyConnected: params.onlyConnected,
      start: params.start,
      end: params.end,
    },
  });
  if (!res || res.success === false) {
    throw new Error(res?.message || "Erreur lors de l'exécution du select.");
  }
  return Array.isArray(res.data) ? res.data : [];
}

export interface UserActionSelectResult {
  statistics: UserActionStatistics | null;
  data: UserActionEntry[];
}

export const userActionApi = {
  select: async (params: UserActionFilters): Promise<UserActionSelectResult> => {
    const rows = await runSelect(params);
    const payload = rows[0] as any;
    return {
      statistics: (payload?.statistics as UserActionStatistics) || null,
      data: (Array.isArray(payload?.data) ? payload.data : []) as UserActionEntry[],
    };
  },

  getDetail: async (id: number): Promise<UserActionEntry | null> => {
    const token = sessionStorage.getItem('blobtic_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await axios.get(`${buildApiUrl(`/api/UserActionData/${id}`)}`, { headers });
    return res.data?.data || null;
  },

  getConnexions: async (userId: number, start: string, end: string, limit = 200): Promise<UserConnexion[]> => {
    const rows = await runSelect({ userId, start, end, limit });
    return (rows || []) as UserConnexion[];
  },

  getConnexionActions: async (cnxIdf: number, limit = 2000): Promise<UserActionEntry[]> => {
    const rows = await runSelect({ cnxIdf, limit });
    return (rows || []) as UserActionEntry[];
  },
};

export default userActionApi;
